import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  KeyRound, Loader2, CheckCircle2, XCircle, Save, Plug, Eye, EyeOff, ShieldCheck,
} from "lucide-react";

/**
 * AdminSettingsPage — the "key vault". Lets the owner rotate every third-party
 * key and business setting without touching code or redeploying. Secrets are
 * stored encrypted; a "Test" button per provider verifies the key live.
 */
const GROUP_META = {
  stripe: { label: "Payments — Stripe", blurb: "Card checkout & subscriptions. Toggle test/live by pasting the matching secret key." },
  resend: { label: "Email & Leads — Resend", blurb: "Transactional emails (password resets, receipts, digests) + the 'PassADC Leads' audience for marketing broadcasts." },
  elevenlabs: { label: "Voice — ElevenLabs", blurb: "Text-to-speech for OSCE voice role-play." },
  ai: { label: "AI / LLM", blurb: "Powers lesson generation, the AI Mentor, and support drafts." },
};

const SOURCE_BADGE = {
  db: { label: "Custom", className: "bg-primary/15 text-primary border-primary/30" },
  env: { label: "Default", className: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
  unset: { label: "Not set", className: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
};

const MODE_BADGE = {
  live: { label: "LIVE", className: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  test: { label: "TEST / SANDBOX", className: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  invalid: { label: "INVALID KEY", className: "bg-red-500/15 text-red-700 border-red-500/30" },
};


const AdminSettingsPage = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});         // key -> new value being typed
  const [reveal, setReveal] = useState({});          // key -> bool
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});        // group -> bool
  const [testResult, setTestResult] = useState({});  // group -> {ok,message}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings");
      setSettings(r.settings || []);
      setDrafts({});
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const values = Object.fromEntries(
      Object.entries(drafts).filter(([, v]) => v !== undefined)
    );
    if (Object.keys(values).length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", { values });
      setSettings(r.settings || []);
      setDrafts({});
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings", { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (group) => {
    setTesting((t) => ({ ...t, [group]: true }));
    setTestResult((r) => ({ ...r, [group]: null }));
    try {
      const r = await api.post(`/admin/settings/test/${group}`);
      setTestResult((prev) => ({ ...prev, [group]: r }));
    } catch (e) {
      setTestResult((prev) => ({ ...prev, [group]: { ok: false, message: "Test request failed" } }));
    } finally {
      setTesting((t) => ({ ...t, [group]: false }));
    }
  };

  const groups = [...new Set(settings.map((s) => s.group))];
  const dirtyCount = Object.keys(drafts).length;

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <KeyRound className="w-6 h-6 text-primary" /> Settings & API keys
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage every integration from here — no code changes or redeploys needed. Keys are stored
          encrypted. Leave a field blank to fall back to the deployment default.
        </p>
      </div>

      <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex gap-2">
        <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          <b>Custom</b> = a value you saved here. <b>Default</b> = the value baked into this
          deployment's environment. Saved custom values always win.
        </span>
      </div>

      {groups.map((group) => {
        const items = settings.filter((s) => s.group === group);
        const meta = GROUP_META[group] || { label: group, blurb: "" };
        const tr = testResult[group];
        return (
          <div key={group} className="rounded-2xl border border-border bg-card p-6" data-testid={`settings-group-${group}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h2 className="text-base font-semibold">{meta.label}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{meta.blurb}</p>
              </div>
              <div className="flex items-center gap-2">
                {tr && (
                  <span className={`text-xs font-medium flex items-center gap-1 ${tr.ok ? "text-emerald-600" : "text-red-600"}`} data-testid={`test-result-${group}`}>
                    {tr.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {tr.message}
                  </span>
                )}
                <button
                  onClick={() => runTest(group)}
                  disabled={testing[group]}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm hover:bg-secondary disabled:opacity-50"
                  data-testid={`test-btn-${group}`}
                >
                  {testing[group] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
                  Test
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {items.map((s) => {
                const badge = SOURCE_BADGE[s.source];
                const draftVal = drafts[s.key];
                const isRevealed = reveal[s.key];
                return (
                  <div key={s.key}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium">{s.label}</label>
                      <div className="flex items-center gap-1.5">
                        {s.mode && (
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${MODE_BADGE[s.mode].className}`}
                            data-testid={`mode-${s.key}`}
                          >
                            {MODE_BADGE[s.mode].label}
                          </span>
                        )}
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.className}`} data-testid={`source-${s.key}`}>
                          {badge.label}
                        </span>
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type={s.is_secret && !isRevealed ? "password" : "text"}
                        value={draftVal !== undefined ? draftVal : (s.is_secret ? "" : s.value)}
                        placeholder={s.is_secret && s.configured ? s.value : "Not set"}
                        onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: e.target.value }))}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid={`input-${s.key}`}
                      />
                      {s.is_secret && (
                        <button
                          type="button"
                          onClick={() => setReveal((r) => ({ ...r, [s.key]: !r[s.key] }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          tabIndex={-1}
                        >
                          {isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{s.help}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="sticky bottom-4 flex items-center justify-end gap-3 rounded-2xl border border-border bg-card/95 backdrop-blur p-4 shadow-lg">
        {dirtyCount > 0 && (
          <span className="text-sm text-muted-foreground">{dirtyCount} unsaved change{dirtyCount > 1 ? "s" : ""}</span>
        )}
        <button
          onClick={save}
          disabled={saving || dirtyCount === 0}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50"
          data-testid="settings-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
